import React, { useEffect, useState, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Globe, Loader2, RefreshCw, Star, Check, Ban, Trash2, Shapes, Plug } from 'lucide-react';

const FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];
const STATUS_COLOR = {
  pending: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  rejected: 'bg-red-500/15 text-red-500 border-red-500/30',
};

export default function CommunityModerationPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [requireApproval, setRequireApproval] = useState(false);
  const [savingSetting, setSavingSetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getCommunityModeration(filter === 'all' ? undefined : filter);
      setItems(res.items || []);
      setCounts(res.counts || { pending: 0, approved: 0, rejected: 0, total: 0 });
    } catch (e) { toast.error(e.message || 'Could not load moderation queue'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getCommunitySettings().then(s => setRequireApproval(!!s.require_approval)).catch(() => {});
  }, []);

  const toggleSetting = async (val) => {
    setRequireApproval(val);
    setSavingSetting(true);
    try { await api.updateCommunitySettings({ require_approval: val }); toast.success(val ? 'New submissions now require approval' : 'Submissions publish instantly'); }
    catch (e) { toast.error(e.message); setRequireApproval(!val); }
    finally { setSavingSetting(false); }
  };

  const moderate = async (t, patch) => {
    try {
      await api.moderateCommunity(t.gallery_id, patch);
      setItems(prev => prev.map(it => it.gallery_id === t.gallery_id ? { ...it, ...patch } : it));
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Permanently delete "${t.name}" from the community gallery?`)) return;
    try { await api.unpublishCommunity(t.gallery_id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="community-moderation-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Globe className="w-6 h-6 text-primary" /> Community Moderation</h1>
          <p className="text-sm text-muted-foreground mt-1">Review, curate and gate custom visuals & connectors shared across tenants.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 rounded-lg text-sm" data-testid="mod-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTERS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}{f.value !== 'all' ? ` (${counts[f.value] || 0})` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="rounded-lg" onClick={load} data-testid="mod-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Approval gate setting */}
      <Card className="p-4 mb-5 flex items-center justify-between" data-testid="mod-approval-setting">
        <div>
          <div className="font-semibold text-sm">Require approval before publishing</div>
          <p className="text-xs text-muted-foreground mt-0.5">When on, new submissions stay hidden from other tenants until you approve them.</p>
        </div>
        <div className="flex items-center gap-2">
          {savingSetting && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          <Switch checked={requireApproval} onCheckedChange={toggleSetting} data-testid="mod-approval-switch" />
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="mod-empty">
          <Globe className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No {filter === 'all' ? '' : filter} submissions.
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(t => {
            const Icon = LucideIcons[t.icon] || (t.kind === 'connector' ? Plug : Shapes);
            return (
              <Card key={t.gallery_id} className={`p-4 ${t.featured ? 'border-amber-400/60 ring-1 ring-amber-400/30' : ''}`} data-testid={`mod-item-${t.gallery_id}`}>
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{t.name}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">{t.kind}</Badge>
                      <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_COLOR[t.status] || ''}`}>{t.status}</Badge>
                      {t.featured && <Badge className="text-[10px] gap-1 bg-amber-500/15 text-amber-500 border-amber-500/30"><Star className="w-2.5 h-2.5 fill-amber-500" />Featured</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.description || '—'}</p>
                    <div className="text-[11px] text-muted-foreground/70 mt-1">by {t.published_by_email || 'unknown'} · {t.published_by_tenant} · {t.downloads || 0} imports</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <div className="flex items-center gap-1">
                      {t.status !== 'approved' && (
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-emerald-500" onClick={() => moderate(t, { status: 'approved' })} data-testid={`mod-approve-${t.gallery_id}`}><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
                      )}
                      {t.status !== 'rejected' && (
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-red-500" onClick={() => moderate(t, { status: 'rejected' })} data-testid={`mod-reject-${t.gallery_id}`}><Ban className="w-3.5 h-3.5 mr-1" />Reject</Button>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className={`h-7 text-[11px] ${t.featured ? 'text-amber-500' : 'text-muted-foreground'}`} onClick={() => moderate(t, { featured: !t.featured })} data-testid={`mod-feature-${t.gallery_id}`}>
                        <Star className={`w-3.5 h-3.5 mr-1 ${t.featured ? 'fill-amber-500' : ''}`} />{t.featured ? 'Unfeature' : 'Feature'}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-red-500" title="Delete" onClick={() => remove(t)} data-testid={`mod-delete-${t.gallery_id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
